<script>
/**
 * 감정 패널티 목록 (실시간 산출).
 * 본사: 전 지점, 지점: 소속 지점만.
 */
import { getAppraisalPenaltyList } from '@/api/appraisalPenalty.js'
import AppPagination from '@/components/common/AppPagination.vue'

export default {
  name: 'PenaltyListPage',
  components: { AppPagination },

  data() {
    return {
      items: [],
      page: 1,
      size: 20,
      loading: false,
      errorMsg: '',
      headers: [
        { title: '구분', key: 'penaltyKindLabel', sortable: true, width: '120px' },
        { title: '지점', key: 'branchName', sortable: true },
        { title: '회원', key: 'userName', sortable: true },
        { title: '현물', key: 'spotType', sortable: true, width: '88px' },
        { title: '지점완료', key: 'completeDt', sortable: true },
        { title: '본사검품', key: 'hqDt', sortable: true },
        { title: '초과(영업일)', key: 'overBusinessDays', sortable: true, align: 'end' },
        { title: '패널티%', key: 'penaltyFeePct', sortable: true, align: 'end' },
        { title: '중량(g)', key: 'weightCol', sortable: false, align: 'end' },
        { title: '적용단가(원/g)', key: 'appliedPriceKrwPerG', sortable: true, align: 'end' },
        { title: '금액(원)', key: 'amountKrw', sortable: true, align: 'end' },
        { title: '산출', key: 'valuationNote', sortable: false, width: '72px' },
      ],
    }
  },
  computed: {
    total() {
      return this.items.length
    },
    pagedItems() {
      const start = (this.page - 1) * this.size
      return this.items.slice(start, start + this.size)
    },
  },

  mounted() {
    this.fetchList()
  },

  methods: {
    async fetchList() {
      this.loading = true
      this.errorMsg = ''
      try {
        const res = await getAppraisalPenaltyList()
        if (res.data?.code === 0) {
          this.items = res.data.items || []
          if ((this.page - 1) * this.size >= this.items.length) {
            this.page = 1
          }
        } else {
          this.errorMsg = res.data?.message || '목록을 불러올 수 없습니다.'
          this.items = []
          this.page = 1
        }
      } catch (e) {
        console.error('[PenaltyListPage] fetchList error:', e)
        this.errorMsg = '목록 조회 중 오류가 발생했습니다.'
        this.items = []
        this.page = 1
      } finally {
        this.loading = false
      }
    },

    formatMoney(n) {
      if (n == null || n === '') return '-'
      return Number(n).toLocaleString('ko-KR')
    },

    formatUnitPrice(n) {
      if (n == null || n === '') return '-'
      return Number(n).toLocaleString('ko-KR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    },

    isManualRequired(item) {
      return item?.calculationStatus === 'MANUAL_REQUIRED'
    },

    formatDt(dt) {
      if (!dt) return '-'
      return String(dt).substring(0, 16).replace('T', ' ')
    },

    weightCol(item) {
      if (item.penaltyKind === 'WEIGHT_MISMATCH') {
        const d = item.weightDiffGrams != null ? item.weightDiffGrams : '-'
        return `차이 ${d}`
      }
      return item.weightGrams != null ? item.weightGrams : '-'
    },

    onPageChange(newPage) {
      this.page = newPage
    },
    onSizeChange(newSize) {
      this.size = newSize
      this.page = 1
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-caption text-medium-emphasis mb-1">감정평가</div>
        <div class="text-h5 font-weight-bold">패널티 목록</div>
        <div class="text-body-2 text-grey mt-1">
          실시간 산출 · 정산 전 참고용 (현물미전달 / 현물감정오류)
        </div>
      </div>
      <v-btn
        color="primary"
        variant="tonal"
        rounded="lg"
        prepend-icon="mdi-refresh"
        :loading="loading"
        @click="fetchList"
      >
        새로고침
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" rounded="lg" class="mb-4">
      {{ errorMsg }}
    </v-alert>

    <v-card rounded="xl" elevation="0" class="penalty-card">
      <v-data-table
        :headers="headers"
        :items="pagedItems"
        hide-default-footer
        class="penalty-table"
        hover
        density="comfortable"
      >
        <template #item.penaltyKindLabel="{ item }">
          <v-chip size="small" :color="item.penaltyKind === 'WEIGHT_MISMATCH' ? 'deep-orange' : 'amber-darken-2'" variant="tonal">
            {{ item.penaltyKindLabel }}
          </v-chip>
        </template>
        <template #item.completeDt="{ item }">
          {{ formatDt(item.completeDt) }}
        </template>
        <template #item.hqDt="{ item }">
          {{ formatDt(item.hqDt) }}
        </template>
        <template #item.overBusinessDays="{ item }">
          {{ item.overBusinessDays != null ? item.overBusinessDays : '—' }}
        </template>
        <template #item.penaltyFeePct="{ item }">
          {{ item.penaltyFeePct != null ? Number(item.penaltyFeePct).toFixed(2) : '—' }}
        </template>
        <template #item.weightCol="{ item }">
          {{ weightCol(item) }}
        </template>
        <template #item.appliedPriceKrwPerG="{ item }">
          {{ formatUnitPrice(item.appliedPriceKrwPerG) }}
        </template>
        <template #item.amountKrw="{ item }">
          <v-chip v-if="isManualRequired(item)" size="small" color="warning" variant="tonal">
            {{ item.calculationStatusLabel || '수기계산필요' }}
          </v-chip>
          <span v-else class="font-weight-bold">{{ formatMoney(item.amountKrw) }}</span>
        </template>
        <template #item.valuationNote="{ item }">
          <v-tooltip v-if="item.valuationNote" location="top" max-width="320">
            <template #activator="{ props }">
              <v-icon v-bind="props" size="20" color="grey">mdi-information-outline</v-icon>
            </template>
            {{ item.valuationNote }}
          </v-tooltip>
          <span v-else>—</span>
        </template>
        <!-- 기본 영문 "No data available" 대신 한 문장만 표시 -->
        <template #no-data>
          <div class="text-body-2 text-medium-emphasis py-10 text-center">
            <template v-if="loading">불러오는 중…</template>
            <template v-else>표시할 패널티가 없습니다.</template>
          </div>
        </template>
      </v-data-table>
      <div class="px-4 pb-4">
        <AppPagination
          :page="page"
          :size="size"
          :total="total"
          @update:page="onPageChange"
          @update:size="onSizeChange"
        />
      </div>
    </v-card>
  </div>
</template>

<style scoped>
.penalty-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
